import { GoogleGenAI } from "@google/genai";

export async function generateThumbnail(description: string, type: 'asset' | 'shot' = 'asset'): Promise<string | null> {
  if (!description || description.trim() === "") {
    throw new Error("Description is required to generate a thumbnail.");
  }

  const apiKey = process.env.GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error("Gemini API key is missing. Please configure it in the AI Studio secrets.");
  }

  const ai = new GoogleGenAI({ apiKey });

  const prompt = type === 'asset'
    ? `A high-quality 3D asset thumbnail for a VFX production. The asset is: ${description}. Studio lighting, neutral mid-grey background, centered composition, professional look, square framing intended for 500x500px output. NO TEXT, NO LETTERS, NO WORDS, NO WATERMARKS.`
    : `A high-quality cinematic shot thumbnail for a VFX production. The shot description is: ${description}. Cinematic lighting, professional look, 16:9 aspect ratio. NO TEXT, NO LETTERS, NO WORDS, NO WATERMARKS.`;

  const aspectRatio = type === 'asset' ? "1:1" : "16:9";

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash-image",
      contents: [
        {
          text: prompt,
        },
      ],
      config: {
        imageConfig: {
          aspectRatio: aspectRatio,
        },
      },
    });

    for (const part of response.candidates?.[0]?.content?.parts || []) {
      if (part.inlineData) {
        return `data:image/png;base64,${part.inlineData.data}`;
      }
    }

    return null;
  } catch (error) {
    console.error("Error generating image:", error);
    throw error;
  }
}

const SYSTEM_INSTRUCTION = `You are the built-in AI Assistant for a comprehensive VFX Bidding & Project Management application. Your goal is to guide users, explain features, and help them troubleshoot.

### App Overview
This app helps VFX Producers and Estimators import data, map columns, calculate costs, and generate bids. The main sections are:
1. **Project Setup**: Define project name, client, and team members.
2. **Documents**: Import CSVs or Google Sheets. This is where users map columns from their client's turnover to the app's internal fields.
3. **Rates**: Define global rate cards (e.g., Senior Compositor = $800/day).
4. **Shots & Assets**: The core bidding tables. Users can edit tasks, days, and see calculated totals.
5. **Top Sheet**: A summary of all costs, including margins, contingencies, and onset supervision.

### Key Features & Workflows
- **CSV/Sheet Import**: Users upload a file. The app detects headers. If the headers are on row 2 (with task names on row 1), the app merges them. "Bid" labels on row 2 are ignored in favor of row 1.
- **Auto Mapping**: In the Documents tab, the app automatically maps columns using built-in rules and learned history. Users can click "Auto Map Columns" to re-apply these rules. This does NOT use AI or API quota.
- **Rate Cards**: Rates are applied globally. If a user changes the "Compositing" rate in the Rates tab, it updates all shots using that task.
- **Thumbnails**: The app can auto-generate thumbnails using AI if the user clicks the magic wand icon on a shot or asset.

### Troubleshooting
- **Spending Cap Error**: If a user asks how to fix a "spending cap" error, give them these exact steps:
  1. Go to the Google Cloud Console (console.cloud.google.com).
  2. Select the project associated with your Gemini API key.
  3. Open the left-hand menu and click on **Billing**.
  4. Ensure you have a standard **Pay-as-you-go** account (not a free trial).
  5. Go to **Budgets & alerts** and increase your budget cap if you have one set.
  6. You do NOT need to buy a new token/key; you just need to adjust the billing settings on your current Google Cloud project.

### Your Persona
- Be extremely helpful, concise, and professional.
- If the user asks how to do something, provide step-by-step instructions based on the app's capabilities.
- You will be provided with the user's current context (what page they are on). Use this to provide relevant advice.
- If they encounter an error, explain what it means and how to fix it (e.g., rate limits, missing mappings).`;

export async function quickChat(prompt: string, context?: string): Promise<string> {
  const apiKey = process.env.GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error("Gemini API key is missing.");
  }

  const ai = new GoogleGenAI({ apiKey });

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3.1-flash-lite-preview",
      contents: context ? `Context: ${context}\n\nQuestion: ${prompt}` : prompt,
      config: {
        systemInstruction: SYSTEM_INSTRUCTION,
      },
    });

    return response.text || "I'm sorry, I couldn't generate a response.";
  } catch (error) {
    console.error("Error in quickChat:", error);
    throw error;
  }
}
